
import { useState, useEffect } from "react";
import { BlogPost, useBlogPosts } from "./useBlogPosts";

export const useBlogPost = (slug: string) => {
  const [post, setPost] = useState<BlogPost | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const { posts } = useBlogPosts();
  
  useEffect(() => {
    const fetchPost = async () => {
      setIsLoading(true);
      try {
        // In a real application, you'd fetch this from Supabase directly by slug
        // For demo purposes, we'll filter the posts we already have
        
        if (posts.length === 0) {
          return; // Wait for posts to load
        }
        
        const foundPost = posts.find(p => p.slug === slug);
        
        if (!foundPost) {
          setPost(null);
          setIsLoading(false);
          return;
        }
        
        setPost(foundPost);
        
        // Find related posts based on category or tags
        const sameCategory = posts.filter(p => 
          p.id !== foundPost.id && 
          p.category === foundPost.category
        );
        
        // Get up to 2 posts from the same category
        const related = sameCategory.slice(0, 2);
        
        // If we don't have enough related posts by category, add some based on tags
        if (related.length < 2 && foundPost.tags && foundPost.tags.length > 0) {
          const relatedByTags = posts.filter(p => 
            p.id !== foundPost.id && 
            !related.find(r => r.id === p.id) &&
            p.tags && p.tags.some(tag => foundPost.tags?.includes(tag))
          );
          
          related.push(...relatedByTags.slice(0, 2 - related.length));
        }
        
        // If we still don't have enough, just add recent posts
        if (related.length < 2) {
          const otherPosts = posts.filter(p => 
            p.id !== foundPost.id && 
            !related.find(r => r.id === p.id)
          );
          
          related.push(...otherPosts.slice(0, 2 - related.length));
        }
        
        setRelatedPosts(related);
        
      } catch (err) {
        console.error("Error fetching blog post:", err);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchPost();
  }, [slug, posts]);
  
  return {
    post,
    isLoading,
    relatedPosts
  };
};
